import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { CheckCircle2, Save, Sparkles, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChapterBeat } from '@/services/supabase/chapter-beats';

export interface DraftBeat {
  tempId: string;
  title: string;
  description: string;
  isDraft: true;
}

export function SceneItem({ 
  beat, 
  index, 
  onUpdate, 
  onDelete,
  onSaveDraft,
  onRegenerate
}: { 
  beat: ChapterBeat | DraftBeat, 
  index: number, 
  onUpdate?: (id: number, updates: any) => Promise<void>,
  onDelete: (id: number | string) => Promise<void>,
  onSaveDraft?: (tempId: string, title: string, description: string) => Promise<void>,
  onRegenerate?: (id: number | string, index: number, currentTitle: string, currentDescription: string) => Promise<{ title: string; description: string } | null>
}) {
  const isDraft = 'isDraft' in beat;
  const [localTitle, setLocalTitle] = useState(beat.title);
  const [localDescription, setLocalDescription] = useState(beat.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const isActuallyDirty = isDraft || localTitle !== (beat as ChapterBeat).title || localDescription !== ((beat as ChapterBeat).description || '');
  const isProposed = isActuallyDirty || isRegenerating;
  const isDirty = isProposed || isSaving;

  useEffect(() => {
    if (!isSaving && !isRegenerating && !isActuallyDirty) {
      if (beat.title !== localTitle) setLocalTitle(beat.title);
      if ((beat.description || '') !== localDescription) setLocalDescription(beat.description || '');
    }
  }, [beat.title, beat.description, isSaving, isRegenerating, isActuallyDirty]);

  const handleSave = async () => {
    if (!isActuallyDirty && !isDraft) return;
    setIsSaving(true);
    
    if (isDraft && onSaveDraft) {
      await onSaveDraft((beat as DraftBeat).tempId, localTitle, localDescription);
    } else if (!isDraft && onUpdate) {
      await onUpdate((beat as ChapterBeat).id, { title: localTitle, description: localDescription });
    }
    
    setIsSaving(false);
  };

  const handleRegenerate = async () => {
    if (!onRegenerate) return;
    setIsRegenerating(true);
    try {
      const result = await onRegenerate(
        isDraft ? (beat as DraftBeat).tempId : (beat as ChapterBeat).id, 
        index,
        localTitle, 
        localDescription
      );
      if (result) {
        setLocalTitle(result.title);
        setLocalDescription(result.description);
      }
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCancelRevert = () => {
    setLocalTitle(beat.title);
    setLocalDescription(beat.description || '');
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="group relative flex gap-4"
    >
      {/* Scene Indicator */}
      <div className="relative z-10 mt-1 flex-shrink-0">
        <div className={cn(
          "w-9 h-9 rounded-xl flex items-center justify-center border transition-all duration-300 shadow-sm",
          (beat as ChapterBeat).is_completed 
            ? "bg-emerald-500 border-emerald-400 text-white shadow-emerald-500/20" 
            : isDraft 
              ? "bg-primary/10 border-primary/30 text-primary shadow-primary/5 animate-pulse"
              : "bg-muted/30 border-border group-hover:border-primary/40 text-muted-foreground group-hover:text-primary backdrop-blur-sm"
        )}>
          {(beat as ChapterBeat).is_completed ? (
            <CheckCircle2 className="h-4.5 w-4.5" />
          ) : isDraft ? (
            <Sparkles className="h-3.5 w-3.5" />
          ) : (
            <span className="text-xs font-bold leading-none">{index + 1}</span>
          )}
        </div>
      </div>

      {/* Content Card */}
      <div className={cn(
        "flex-1 flex flex-col gap-3 p-4 rounded-2xl border transition-all duration-300",
        "bg-card/40 backdrop-blur-md shadow-sm",
        (beat as ChapterBeat).is_completed 
          ? "border-emerald-500/20 bg-emerald-500/[0.02]" 
          : (isDraft || isProposed)
            ? "border-primary/30 bg-primary/[0.02] shadow-primary/5"
            : "border-border/60 hover:border-primary/30 hover:shadow-md hover:bg-muted/5",
        (isDirty || isDraft) && "border-primary/40 ring-1 ring-primary/10 shadow-lg shadow-primary/5 bg-primary/[0.01]"
      )}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 flex flex-col gap-1">
            {(isDraft || isRegenerating || (isActuallyDirty && !isSaving)) && (
              <span className="text-[9px] font-bold text-primary uppercase tracking-widest px-1">
                {isRegenerating ? "AI Brainstorming..." : "Proposed Revision"}
              </span>
            )}
            <input
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              disabled={isRegenerating || isSaving}
              className={cn(
                "bg-transparent border-none p-0 text-[13px] font-semibold focus:ring-0 w-full placeholder:text-muted-foreground/40 text-foreground",
                isRegenerating && "animate-pulse text-muted-foreground"
              )}
              placeholder={isRegenerating ? "Brainstorming title..." : "Untitled Scene"}
            />
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground/40 hover:text-primary hover:bg-primary/10 rounded-lg"
              onClick={handleRegenerate}
              disabled={isRegenerating || isSaving}
              title="Regenerate with AI"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", isRegenerating && "animate-spin")} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10 rounded-lg"
              onClick={() => onDelete(isDraft ? (beat as DraftBeat).tempId : (beat as ChapterBeat).id)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <div className="relative">
          <Textarea 
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            disabled={isRegenerating || isSaving}
            className={cn(
              "text-[12px] leading-relaxed min-h-[80px] bg-transparent border-none p-0 resize-none focus-visible:ring-0 text-muted-foreground scrollbar-hide",
              isRegenerating && "animate-pulse opacity-50"
            )}
            placeholder={isRegenerating ? "Weaving the story..." : "Describe what happens in this scene..."}
          />
          {isRegenerating && (
             <div className="absolute inset-0 flex items-center justify-center bg-background/20 backdrop-blur-[1px] rounded-lg">
                <Loader2 className="h-4 w-4 animate-spin text-primary/40" />
             </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-1 pt-3 border-t border-border/40">
          {!isDraft ? (
            <div className="flex items-center gap-2">
              <Checkbox 
                id={`complete-${(beat as ChapterBeat).id}`}
                checked={(beat as ChapterBeat).is_completed}
                onCheckedChange={(checked) => onUpdate?.((beat as ChapterBeat).id, { is_completed: !!checked })}
                className="h-3.5 w-3.5 rounded border-muted-foreground/30 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
              />
              <label 
                htmlFor={`complete-${(beat as ChapterBeat).id}`}
                className="text-[10px] font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
              >
                Mark Done
              </label>
            </div>
          ) : (
            <div className="text-[10px] text-muted-foreground/60 italic">
              AI generated this scene...
            </div>
          )}

          <AnimatePresence>
            {isDirty && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, x: 5 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.9, x: 5 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-1.5"
              >
                {!isDraft && !isSaving && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelRevert}
                    className="h-7 px-2 text-[10px] text-muted-foreground hover:text-foreground rounded-lg"
                    title="Revert to original"
                  >
                    Cancel
                  </Button>
                )}
                <Button 
                  variant="default"
                  size="sm" 
                  onClick={handleSave}
                  disabled={isSaving || !isActuallyDirty}
                  className={cn(
                    "h-7 px-3 text-[10px] gap-1.5 font-semibold rounded-lg active:scale-95 transition-all shadow-sm",
                    isSaving ? "bg-primary/80 opacity-90 cursor-not-allowed" : "bg-primary hover:bg-primary/90"
                  )}
                >
                  {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  {isSaving ? 'Persisting...' : (isDraft ? 'Approve & Save' : 'Save Changes')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
